import React, { useEffect, useState } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { useLoaderData } from "react-router-dom";
import { IsNullOrEmpty } from "../../../services/CustomService";
import { approvePendingListing, getPendingListings, rejectPendingListing, updatePendingListing, updatePendingListingPicture } from "../../../data/admin/pendingListings";
import { sourceLabel } from "../user/list";
import { getToolCategories } from "../../../data/loca/tools";

export async function loader() {
    const [listings, toolCategories] = await Promise.all([
        getPendingListings(),
        getToolCategories()
    ]);
    return { listings, toolCategories };
}

export default function PendingListings() {
    const { listings, toolCategories } = useLoaderData();
    const [items, setItems] = useState(listings ?? []);
    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const maxPictureSize = 1_000_000;
    const [selectedListing, setSelectedListing] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [picturePreviewVisible, setPicturePreviewVisible] = useState(false);
    const [picturePreview, setPicturePreview] = useState({ src: '', alt: '' });
    const [formValues, setFormValues] = useState({
        name: '',
        description: '',
        smallDescription: '',
        categoryId: null,
        secondaryCategoryIds: [],
        customUrl: ''
    });

    useEffect(() => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            name: { value: null, matchMode: FilterMatchMode.CONTAINS },
            ownerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            ownerEmail: { value: null, matchMode: FilterMatchMode.CONTAINS }
        });
    }, []);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const getListingPictures = (listing) => {
        const apiPictures = (listing?.pictures ?? listing?.Pictures ?? [])
            .map((picture) => picture.base64Picture ?? picture.Base64Picture)
            .filter((picture) => !IsNullOrEmpty(picture));

        if (apiPictures.length > 0) {
            return apiPictures;
        }

        return IsNullOrEmpty(listing?.base64Picture) ? [] : [listing.base64Picture];
    };

    const openPicturePreview = (rowData, pictureSrc = null) => {
        const src = pictureSrc ?? rowData?.base64Picture;
        if (IsNullOrEmpty(src)) {
            return;
        }

        setPicturePreview({
            src,
            alt: rowData.name ?? 'Photo annonce'
        });
        setPicturePreviewVisible(true);
    };

    const nameBodyTemplate = (rowData) => (
        <div className="flex align-items-center gap-2">
            {(!IsNullOrEmpty(rowData.base64Picture)) ? (
                <img
                    className="smallPicture"
                    alt={rowData.name}
                    src={rowData.base64Picture || null}
                    onClick={() => openPicturePreview(rowData)}
                    style={{ cursor: "pointer" }}
                />
            ) : (
                <div className="no-picture no-picture-small"></div>
            )}
            <span>{rowData.name}</span>
        </div>
    );

    const priceBodyTemplate = (rowData) => {
        return `${rowData.priceDayCents / 100}\u20ac`;
    };

    const createdBodyTemplate = (rowData) => {
        return new Date(rowData.createdAt).toLocaleDateString('fr-FR');
    };

    const ownerBodyTemplate = (rowData) => (
        <div>
            <div>{rowData.ownerName}</div>
            <div className="text-muted">{rowData.ownerEmail}</div>
        </div>
    );

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2">
            <Button
                label="Modifier"
                size="small"
                severity="info"
                onClick={() => handleDetails(rowData)}
            />
            <Button
                label="Valider"
                size="small"
                severity="success"
                onClick={() => handleApprove(rowData)}
            />
            <Button
                label="Refuser"
                size="small"
                severity="danger"
                onClick={() => handleReject(rowData)}
            />
        </div>
    );

    const handlePictureChange = (event, rowData) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        if (!file.type.startsWith('image/')) {
            window.alert('Le fichier doit être une image.');
            event.target.value = '';
            return;
        }

        if (file.size > maxPictureSize) {
            window.alert("L'image doit faire au maximum 1 Mo.");
            event.target.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onloadend = async () => {
            const base64Picture = reader.result;
            const response = await updatePendingListingPicture(rowData.publicId, base64Picture);
            if (response?.status === 200) {
                const updatedPicture = response.base64Picture ?? base64Picture;
                setItems((prev) => prev.map((item) => item.publicId === rowData.publicId
                    ? { ...item, base64Picture: updatedPicture, pictures: [{ base64Picture: updatedPicture }] }
                    : item));
                setSelectedListing((previous) => previous?.publicId === rowData.publicId
                    ? { ...previous, base64Picture: updatedPicture, pictures: [{ base64Picture: updatedPicture }] }
                    : previous);
            }
        };
        reader.readAsDataURL(file);
        event.target.value = '';
    };

    const handleDetails = (rowData) => {
        setSelectedListing(rowData);
        setFormValues({
            name: rowData.name ?? '',
            description: rowData.description ?? '',
            smallDescription: rowData.smallDescription ?? '',
            categoryId: rowData.categoryId ?? null,
            secondaryCategoryIds: rowData.secondaryCategoryIds ?? [],
            customUrl: rowData.customUrl ?? ''
        });
        setDetailsVisible(true);
    };

    const changedFields = selectedListing?.changeSet?.fields ?? selectedListing?.changeSet?.Fields ?? [];


    const handleApprove = async (rowData) => {
        if (!window.confirm("Valider cette annonce ?")) {
            return;
        }
        await approvePendingListing(rowData.publicId);
        setItems((prev) => prev.filter((item) => item.publicId !== rowData.publicId));
    };

    const handleUpdateListing = async () => {
        if (!selectedListing) {
            return;
        }
        const response = await updatePendingListing(selectedListing.publicId, {
            name: formValues.name,
            description: formValues.description,
            smallDescription: formValues.smallDescription,
            categoryId: formValues.categoryId,
            secondaryCategoryIds: (formValues.secondaryCategoryIds ?? []).filter((id) => id !== formValues.categoryId),
            customUrl: formValues.customUrl
        });
        const updatedListing = {
            ...selectedListing,
            publicId: response?.publicId ?? response?.PublicId ?? selectedListing.publicId,
            name: response?.name ?? response?.Name ?? formValues.name,
            description: response?.description ?? response?.Description ?? formValues.description,
            smallDescription: response?.smallDescription ?? response?.SmallDescription ?? formValues.smallDescription,
            category: response?.category ?? response?.Category ?? selectedListing.category,
            categoryId: response?.categoryId ?? response?.CategoryId ?? formValues.categoryId,
            secondaryCategoryIds: response?.secondaryCategoryIds ?? response?.SecondaryCategoryIds ?? formValues.secondaryCategoryIds,
            secondaryCategories: response?.secondaryCategories ?? response?.SecondaryCategories ?? selectedListing.secondaryCategories,
            customUrl: response?.customUrl ?? response?.CustomUrl ?? formValues.customUrl,
            pictures: response?.pictures ?? response?.Pictures ?? selectedListing.pictures,
            changeSet: response?.changeSet ?? response?.ChangeSet ?? selectedListing.changeSet
        };
        setItems((prev) => prev.map((item) => (item.publicId === updatedListing.publicId
            ? {
                ...item,
                name: updatedListing.name,
                description: updatedListing.description,
                smallDescription: updatedListing.smallDescription,
                category: updatedListing.category,
                categoryId: updatedListing.categoryId,
                secondaryCategoryIds: updatedListing.secondaryCategoryIds,
                secondaryCategories: updatedListing.secondaryCategories,
                customUrl: updatedListing.customUrl,
                pictures: updatedListing.pictures,
                changeSet: updatedListing.changeSet
            }
            : item)));
        setSelectedListing(updatedListing);
        setFormValues({
            name: updatedListing.name ?? '',
            description: updatedListing.description ?? '',
            smallDescription: updatedListing.smallDescription ?? '',
            categoryId: updatedListing.categoryId ?? null,
            secondaryCategoryIds: updatedListing.secondaryCategoryIds ?? [],
            customUrl: updatedListing.customUrl ?? ''
        });
        setDetailsVisible(false);
        setSelectedListing(null);
    };

    const handleDeletePicture = async (pictureIndex) => {
        if (!selectedListing) {
            return;
        }

        const pictures = getListingPictures(selectedListing);
        if (pictures.length <= 1) {
            window.alert("Une annonce doit conserver au moins une photo.");
            return;
        }

        if (!window.confirm("Supprimer cette photo de l'annonce ?")) {
            return;
        }

        const nextPictures = pictures.filter((_, index) => index !== pictureIndex);
        const response = await updatePendingListing(selectedListing.publicId, {
            base64Picture: nextPictures[0] ?? '',
            additionalPictures: nextPictures.slice(1)
        });

        const updatedListing = {
            ...selectedListing,
            base64Picture: response?.base64Picture ?? response?.Base64Picture ?? nextPictures[0],
            pictures: response?.pictures ?? response?.Pictures ?? nextPictures.map((picture, index) => ({
                base64Picture: picture,
                sortOrder: index
            })),
            changeSet: response?.changeSet ?? response?.ChangeSet ?? selectedListing.changeSet
        };

        setItems((prev) => prev.map((item) => item.publicId === updatedListing.publicId
            ? {
                ...item,
                base64Picture: updatedListing.base64Picture,
                pictures: updatedListing.pictures,
                changeSet: updatedListing.changeSet
            }
            : item));
        setSelectedListing(updatedListing);
    };

    const handleReject = async (rowData) => {
        if (!window.confirm("Refuser cette annonce ?")) {
            return;
        }
        await rejectPendingListing(rowData.publicId);
        setItems((prev) => prev.filter((item) => item.publicId !== rowData.publicId));
    };

    const header = (
        <div className="flex justify-content-between">
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Rechercher..." />
            </span>
        </div>
    );

    return (
        <div>
            <h1>Validation des annonces</h1>
            <div className="card">
                <DataTable value={items} paginator showGridlines rows={25} dataKey="publicId" filters={filters} globalFilterFields={['name', 'ownerName', 'ownerEmail']} header={header} emptyMessage="Aucune annonce en attente">
                    <Column header="Annonce" body={nameBodyTemplate} />
                    <Column field="brand" header="Marque" />
                    <Column field="model" header="Mod&egrave;le" />
                    <Column header="Cat&eacute;gorie" body={(rowData) => [rowData.category, ...(rowData.secondaryCategories ?? [])].filter(Boolean).join(', ')} />
                    <Column header="Prix/j" body={priceBodyTemplate} />
                    <Column header="Propri&eacute;taire" body={ownerBodyTemplate} />
                    <Column header="Cr&eacute;&eacute;e le" body={createdBodyTemplate} />
                    <Column header="Source" body={(rowData) => sourceLabel(rowData.creationSource)} />
                    <Column header="" body={actionBodyTemplate} />
                </DataTable>
            </div>
            <Dialog
                header="Détails de l'annonce"
                visible={detailsVisible}
                style={{ width: '50vw' }}
                baseZIndex={2000}
                appendTo={typeof window !== 'undefined' ? document.body : undefined}
                onHide={() => {
                    setDetailsVisible(false);
                    setSelectedListing(null);
                }}
                footer={(
                    <div className="flex justify-content-end gap-2">
                        <Button label="Enregistrer" size="small" onClick={handleUpdateListing} />
                        <Button
                            label="Fermer"
                            size="small"
                            severity="secondary"
                            onClick={() => {
                                setDetailsVisible(false);
                                setSelectedListing(null);
                            }}
                        />
                    </div>
                )}
            >
                {selectedListing ? (
                    <div className="flex flex-column gap-3">
                        <div className="flex flex-column gap-2">
                            <label htmlFor="listing-name"><strong>Nom</strong></label>
                            <InputText
                                id="listing-name"
                                value={formValues.name}
                                onChange={(event) => setFormValues((prev) => ({ ...prev, name: event.target.value }))}
                            />
                            <span className="text-muted">
                                {selectedListing.brand} {selectedListing.model}
                            </span>
                        </div>
                        {changedFields.length > 0 ? (
                            <div className="flex flex-column gap-2">
                                <strong>Modifications demandées</strong>
                                <ul className="m-0 pl-3">
                                    {changedFields.map((field) => (
                                        <li key={field.field ?? field.Field} className="mb-2">
                                            <div><strong>{field.field ?? field.Field}</strong></div>
                                            <div className="text-muted">Avant : {field.previousValue ?? field.PreviousValue ?? '—'}</div>
                                            <div>Après : {field.currentValue ?? field.CurrentValue ?? '—'}</div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                        <div className="flex flex-column gap-2">
                            <label htmlFor="listing-picture"><strong>Photo principale</strong></label>
                            <input
                                id="listing-picture"
                                type="file"
                                accept="image/*"
                                onChange={(event) => handlePictureChange(event, selectedListing)}
                            />
                        </div>
                        <div className="flex flex-column gap-2">
                            <strong>Photos à valider</strong>
                            {getListingPictures(selectedListing).length > 0 ? (
                                <div className="admin-listing-gallery">
                                    {getListingPictures(selectedListing).map((picture, index) => (
                                        <div
                                            className="admin-listing-gallery__item"
                                            role="button"
                                            tabIndex={0}
                                            key={`${picture}-${index}`}
                                            onClick={() => openPicturePreview(selectedListing, picture)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    openPicturePreview(selectedListing, picture);
                                                }
                                            }}
                                            aria-label={`Voir la photo ${index + 1}`}
                                        >
                                            <img src={picture} alt="" />
                                            {index === 0 ? <span>Principale</span> : null}
                                            {getListingPictures(selectedListing).length > 1 ? (
                                                <Button
                                                    type="button"
                                                    icon="pi pi-trash"
                                                    rounded
                                                    text
                                                    severity="danger"
                                                    className="admin-listing-gallery__delete"
                                                    aria-label={`Supprimer la photo ${index + 1}`}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleDeletePicture(index);
                                                    }}
                                                    onKeyDown={(event) => {
                                                        event.stopPropagation();
                                                    }}
                                                />
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="no-picture no-picture-small"></div>
                            )}
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="listing-category"><strong>Catégorie</strong></label>
                            <Dropdown
                                id="listing-category"
                                value={formValues.categoryId}
                                options={toolCategories}
                                optionLabel="name"
                                optionValue="value"
                                placeholder="Sélectionner une catégorie"
                                onChange={(event) => setFormValues((prev) => ({ ...prev, categoryId: event.value }))}
                            />
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="listing-secondary-categories"><strong>Catégories secondaires</strong></label>
                            <MultiSelect
                                id="listing-secondary-categories"
                                value={(formValues.secondaryCategoryIds ?? []).filter((id) => id !== formValues.categoryId)}
                                options={toolCategories.filter((category) => category.value !== formValues.categoryId)}
                                optionLabel="name"
                                optionValue="value"
                                display="chip"
                                placeholder="Aucune"
                                onChange={(event) => setFormValues((prev) => ({ ...prev, secondaryCategoryIds: event.value }))}
                            />
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="listing-custom-url"><strong>URL personnalisée</strong></label>
                            <InputText
                                id="listing-custom-url"
                                value={formValues.customUrl}
                                onChange={(event) => setFormValues((prev) => ({ ...prev, customUrl: event.target.value }))}
                            />
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="listing-description"><strong>Description</strong></label>
                            <InputTextarea
                                id="listing-description"
                                value={formValues.description}
                                rows={4}
                                onChange={(event) => setFormValues((prev) => ({ ...prev, description: event.target.value }))}
                            />
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="listing-small-description"><strong>Description courte</strong></label>
                            <InputTextarea
                                id="listing-small-description"
                                value={formValues.smallDescription}
                                rows={3}
                                onChange={(event) => setFormValues((prev) => ({ ...prev, smallDescription: event.target.value }))}
                            />
                        </div>
                    </div>
                ) : null}
            </Dialog>
            <Dialog
                header="Photo de l'annonce"
                visible={picturePreviewVisible}
                style={{ width: '60vw' }}
                baseZIndex={2100}
                appendTo={typeof window !== 'undefined' ? document.body : undefined}
                onHide={() => {
                    setPicturePreviewVisible(false);
                    setPicturePreview({ src: '', alt: '' });
                }}
            >
                {picturePreview.src ? (
                    <img
                        alt={picturePreview.alt}
                        src={picturePreview.src}
                        style={{ width: '100%', borderRadius: '8px' }}
                    />
                ) : (
                    <div className="no-picture no-picture-large"></div>
                )}
            </Dialog>
        </div>
    );
}
